import { Box, Text, Flex } from "@chakra-ui/react";
import Menubar from "../components/Menubar";
import { ACCENT_1_COLOR, ACCENT_3_COLOR, PRIMARY_COLOR } from "../constants/color";
import CustomCard from "../components/CustomCard";
import { GiKatana, GiGlobe, GiCalendar, GiEnvelope } from "react-icons/gi";
import { MdAllInclusive } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import background from "../assets/bg.jpg"
import { useTranslation } from "react-i18next";
import kenjutsuImage from "../assets/kenjutsu.jpg";
import kataImage from "../assets/kata.jpg";
import tameshigiriVideo from "../assets/tameshigiri.webm"
import jitaKyoeiImage from "../assets/jitaKyoei.jpg"
import zazenImage from "../assets/zazen.jpeg"
import { Helmet } from 'react-helmet-async';

export default function Home() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const practices = [
    {
      title: "Iaido Kata",
      titleDesc: "(居合形)",
      description: t("kataDesc"),
      image: kataImage,
      isVideo: false
    },
    {
      title: "Kumitachi ​/ Kenjutsu",
      titleDesc: "(組太刀 / ​剣術)",
      description: t("kumitachiDesc"),
      image: kenjutsuImage,
      isVideo: false
    },
    {
      title: "Tameshigiri",
      titleDesc: "(試し斬り)",
      badge: "Cutting",
      description: t("tameshigiriDesc"),
      image: tameshigiriVideo,
      isVideo: true
    },
        {
      title: "Jita-Kyoei",
      titleDesc: "(自他共栄)",
      description: t("jitaKyoeiDesc"),
      image: jitaKyoeiImage,
      isVideo: false,
    },
        {
      title: t("zazen"),
      titleDesc: t("zazen2"),
      description: t("zazenDesc"),
      image: zazenImage,
      isVideo: false
    }
  ];

  const schemaMarkup = {
    "@context": "https://schema.org",
    "@type": "MartialArtsSchool",
    "name": "Mugai Ryu Indonesia",
    "image": "https://mugairyu-indonesia.com/logo.png",
    "url": "https://mugairyu-indonesia.com/",
    "description": "Pelajari seni pedang samurai tradisional Jepang Mugai Ryu Iaido resmi di Indonesia. Terbuka untuk kelas pemula hingga tingkat lanjut."
  };

  return (
    <Box display={"flex"} width={"100vw"} pb={"24px"} overflowX="hidden" background={`transparent url(${background})`}
        bgRepeat={"repeat"}>
      <Helmet>
        <title>{t("homeTitle")}</title>
        <meta name="description" content={t("homeTitleDesc")} />
        <link rel="canonical" href="https://mugairyu-indonesia.com/" />
        
        {/* Open Graph untuk Ringkasan AI & Media Sosial */}
        <meta property="og:title" content={t("homeTitle")} />
        <meta property="og:description" content={t("homeTitleDesc2")} />
        <meta property="og:image" content="https://mugairyu-indonesia.com/logo.png" />
        <meta property="og:url" content="https://mugairyu-indonesia.com/" />
        <meta property="og:type" content="website" />

        {/* Schema Markup */}
        <script type="application/ld+json">
          {JSON.stringify(schemaMarkup)}
        </script>
      </Helmet>
      <Box width={"100vw"} height={"auto"}>
        <Menubar />

        {/* SECTION 1: HERO CONTAINER */}
        <Flex 
          height={{ base: "auto", md: "750px" }} 
          alignItems={"center"} 
          justifyContent="center"
          mx={{ base: "24px", md: "10vw", lg: "20vw" }} 
          gap={{ base: "40px", md: "5vw" }}
          flexDir={{ base: "column", md: "row" }}
          py={{ base: "60px", md: "0px" }}
        >
          <Box display={"flex"} alignItems={"center"} justifyContent="center">
            <Text
              textColor={ACCENT_1_COLOR}
              fontSize={{ base: "70px", md: "100px" }}
              className="yuji-syuku-regular"
              lineHeight={{ base: "80px", md: "110px" }}
              textAlign={{ base: "center", md: "left" }}
            >
              無<br/>外<br/>流
            </Text>
          </Box>
          <Box display={"flex"} flexDir={"column"} textAlign={{ base: "center", md: "left" }}>
            <Text
              textColor={ACCENT_1_COLOR}
              fontSize={{ base: "48px", md: "80px" }}
              className="cormorant-garamond-normal"
              lineHeight={{ base: "54px", md: "80px" }}
              mb="16px"
            >
              MUGAI RYU
            </Text>
            <Text
              textColor={ACCENT_1_COLOR}
              fontSize={{ base: "18px", md: "24px" }}
              mb={"18px"}
              className="cormorant-garamond-normal"
              lineHeight={{ base: "26px", md: "34px" }}
            >
              “Ippo Jitsu Mugai, Kenkon Toku Ittei.”<br/>
              {t("subtitleHome1")}<br/>
              {t("subtitleHome2")}
            </Text>
            <Text
              textColor={ACCENT_3_COLOR}
              fontSize={"18px"}
              className="cormorant-garamond-normal"
              lineHeight={"26px"}
            >
              {t("descriptionHome")}
            </Text>
          </Box>
        </Flex>

        {/* SECTION 2: OUR PRACTICE */}
        <Box display={"flex"} alignItems={"center"} mx={{ base: "24px", md: "10vw", lg: "20vw" }} flexDir={"column"} mb={"100px"}>
          <Box display={"flex"} flexDir={"column"} w={{base: "100%", md: "100%", lg: "90%"}}>
            <Text textColor={ACCENT_1_COLOR} fontSize={"42px"} className="cormorant-garamond-normal" mb={"24px"} textAlign={{ base: "center" }}>
              {t("ourPractice")}
            </Text>
            <Flex gap={"24px"} flexDir={"row"} flexWrap="wrap" justifyContent="center">
              {practices.map((practice, index) => (
                <Box key={index} display="flex" justifyContent="center">
                  <CustomCard
                    title={practice.title}
                    titleDesc={practice.titleDesc}
                    badge={practice.badge}
                    description={practice.description}
                    image={practice.image}
                    isVideo={practice.isVideo}
                  />
                </Box>
              ))}
            </Flex>
          </Box>
        </Box>

        {/* SECTION 3: KEIKO (SPIRIT OF PRACTICE) */}
        <Box display={"flex"} alignItems={"center"} mx={{ base: "24px", md: "10vw", lg: "20vw" }} flexDir={"column"} mb={"150px"}>
          <Box display={"flex"} flexDir={"column"} w="100%" alignItems={{ base: "center" }}>
            <Text textColor={ACCENT_1_COLOR} fontSize={"42px"} className="cormorant-garamond-normal" lineHeight={"42px"}>
              Keiko
            </Text>
            <Text textColor={ACCENT_1_COLOR} fontSize={"24px"} className="cormorant-garamond-normal" mb={"36px"} lineHeight={"32px"}>
              {t("keikoDesc")}
            </Text>
            
            <Flex gap={{ base: "48px", lg: "24px" }} flexDir={{ base: "column", md: "row" }} flexWrap="wrap" justifyContent="center" w="100%">

              {/* Card 2: Inclusivity */}
              <Box display={"flex"} flexDirection={"column"} alignItems={"center"} w={{ base: "100%", md: "220px", lg: "250px" }} textAlign="center">
                <MdAllInclusive size={42} color={ACCENT_1_COLOR}/>
                <Text py="8px" className='cormorant-garamond-bold' color={ACCENT_1_COLOR} fontSize={"22px"}>
                  {t("inclusivity")}
                </Text>
                <Text className='cormorant-garamond-normal' color={PRIMARY_COLOR} align={"justify"}>
                  {t("inclusivityDesc")}
                </Text>
              </Box>

              {/* Card 3: Equipment */}
              <Box display={"flex"} flexDirection={"column"} alignItems={"center"} w={{ base: "100%", md: "220px", lg: "250px" }} textAlign="center">
                <GiKatana size={42} color={ACCENT_1_COLOR}/>
                <Text py="8px" className='cormorant-garamond-bold' color={ACCENT_1_COLOR} fontSize={"22px"}>
                  {t("equipment")}
                </Text>
                <Text className='cormorant-garamond-normal' color={PRIMARY_COLOR} align={"justify"}>
                  {t("equipmentDesc")}
                </Text>
              </Box>
            </Flex>
          </Box>
        </Box>

        {/* SECTION 4: LINEAGE CTA */}
        <Box display={"flex"} alignItems={"center"} mx={{ base: "24px", md: "10vw", lg: "20vw" }} flexDir={"column"} mb={"100px"}>
          <Box display={"flex"} flexDir={"column"} alignItems={"center"} w={"100%"}>
            <Text textColor={ACCENT_1_COLOR} fontSize={{ base: "32px", md: "42px" }} className="cormorant-garamond-normal" lineHeight={{ base: "38px", md: "42px" }} textAlign={"center"}>
              {t("branch")}
            </Text>
            <Text textColor={ACCENT_1_COLOR} fontSize={"24px"} className="cormorant-garamond-normal" mb={"24px"} lineHeight={"32px"} textAlign={"center"}>
              {t("branchSubtitle")}
            </Text>

            <Box display={"flex"} flexDir={"column"} alignItems={"center"} maxW={"760px"}>
              <Box display={"flex"} flexDirection={"column"} alignItems={"center"} mb={"32px"} textAlign={"center"}>
                <GiGlobe size={42} color={ACCENT_1_COLOR} />
                <Text py="16px" className='cormorant-garamond-normal' color={PRIMARY_COLOR} lineHeight={"28px"}>
                  {t("branchDesc")}<br/>
                </Text>
              </Box>

              <Box
                as="button"
                px={"24px"}
                py={"12px"}
                border={`1px solid ${ACCENT_1_COLOR}`}
                color={ACCENT_1_COLOR}
                className="cormorant-garamond-normal"
                fontSize={"16px"}
                letterSpacing={"2px"}
                textTransform={"uppercase"}
                transition={"all 0.3s ease"}
                _hover={{
                  bg: ACCENT_1_COLOR,
                  color: "#F7F5F0",
                }}
                onClick={() => navigate("/lineage")}
              >
                [ {t("homeLineageButton")} ]
              </Box>
            </Box>
          </Box>
        </Box>

        {/* SECTION 5: FINAL FUNNEL MAT CONTACT */}
        <Box display={"flex"} alignItems={"center"} mx={{ base: "24px", md: "10vw", lg: "20vw" }} flexDir={"column"} mb={"100px"}>
          <Box display={"flex"} flexDir={"column"} alignItems={"center"} w={"100%"}>
            <Text textColor={ACCENT_1_COLOR} fontSize={{ base: "32px", md: "42px" }} className="cormorant-garamond-normal" lineHeight={{ base: "38px", md: "42px" }} textAlign={"center"}>
              {t("welcomeToDojo")}
            </Text>
            <Text textColor={ACCENT_1_COLOR} fontSize={"24px"} className="cormorant-garamond-normal" mb={"48px"} lineHeight={"32px"} textAlign={"center"}>
              {t("joinUs")}
            </Text>

            <Flex gap={"64px"} flexDir={{ base: "column", md: "row" }} alignItems={{ base: "center", md: "stretch" }} justifyContent="center" w="100%">
              {/* Left Column: Schedule */}
              <Box display={"flex"} flexDirection={"column"} alignItems={"center"} w={{ base: "100%", sm: "300px" }} textAlign={"center"} justifyContent={"space-between"}>
                <Box display={"flex"} flexDirection={"column"} alignItems={"center"}>
                  <GiCalendar size={42} color={ACCENT_1_COLOR} />
                  <Text py="8px" className='cormorant-garamond-bold' color={ACCENT_1_COLOR} fontSize={"24px"}>
                    {t("seeUsInAction")}
                  </Text>
                  <Text className='cormorant-garamond-normal' color={PRIMARY_COLOR} mb={"24px"}>
                    {t("seeUsInActionDesc")}
                  </Text>
                </Box>
                <Box
                  as="button"
                  w={"100%"}
                  py={"12px"}
                  border={`1px solid ${ACCENT_1_COLOR}`}
                  color={ACCENT_1_COLOR}
                  className="cormorant-garamond-normal"
                  fontSize={"15px"}
                  letterSpacing={"2px"}
                  textTransform={"uppercase"}
                  transition={"all 0.3s ease"}
                  _hover={{
                    bg: ACCENT_1_COLOR,
                    color: "#F7F5F0",
                  }}
                  onClick={() => navigate("/our-dojos")}
                >
                  [ {t("viewTrainingSchedule")} ]
                </Box>
              </Box>

              {/* Right Column: Contact */}
              <Box display={"flex"} flexDirection={"column"} alignItems={"center"} w={{ base: "100%", sm: "300px" }} textAlign={"center"} justifyContent={"space-between"}>
                <Box display={"flex"} flexDirection={"column"} alignItems={"center"}>
                  <GiEnvelope size={42} color={ACCENT_1_COLOR} />
                  <Text py="8px" className='cormorant-garamond-bold' color={ACCENT_1_COLOR} fontSize={"24px"}>
                    {t("inquireDirectly")}
                  </Text>
                  <Text className='cormorant-garamond-normal' color={PRIMARY_COLOR} mb={"24px"}>
                    {t("inquireDirectlyDesc")}
                  </Text>
                </Box>
                <Box
                  as="button"
                  w={"100%"}
                  py={"12px"}
                  border={`1px solid ${ACCENT_1_COLOR}`}
                  color={ACCENT_1_COLOR}
                  className="cormorant-garamond-normal"
                  fontSize={"15px"}
                  letterSpacing={"2px"}
                  textTransform={"uppercase"}
                  transition={"all 0.3s ease"}
                  _hover={{
                    bg: ACCENT_1_COLOR,
                    color: "#F7F5F0",
                  }}
                  onClick={() => navigate("/contact")}
                >
                  [ {t("contactTheDojo")} ]
                </Box>
              </Box>
            </Flex>

          </Box>
        </Box>

      </Box>
    </Box>
  );
}